import React from 'react';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { ScoreBreakdown } from '../ScoreBreakdown';
import { CandidateActions } from './CandidateActions';

export const CandidateCard = ({ candidate, recommended, roleIndex, candidateIndex, onNoteAdded, onSubmissionToggled, onStageChanged, selectable, selected, onToggleSelect }) => {
  const c = candidate;
  const testIdPrefix = recommended ? `recommended-${roleIndex}-${candidateIndex}` : `candidate-${roleIndex}-${candidateIndex}`;

  if (recommended) {
    return (
      <div className="group p-2.5 bg-gradient-to-r from-[#D4AF37]/5 to-white border border-[#D4AF37]/30 rounded-lg" data-testid={testIdPrefix}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {selectable && (
              <input type="checkbox" checked={!!selected} onChange={() => onToggleSelect && onToggleSelect(c)}
                className="h-4 w-4 rounded border-gray-300 text-[#D4AF37] focus:ring-[#D4AF37] flex-shrink-0 cursor-pointer"
                onClick={e => e.stopPropagation()} data-testid={`select-rec-${roleIndex}-${candidateIndex}`} />
            )}
            <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ring-2 ${c.fit_score >= 50 ? 'bg-emerald-100 ring-emerald-300' : 'bg-amber-100 ring-amber-300'}`}>
              <span className={`text-[10px] font-bold ${c.fit_score >= 50 ? 'text-emerald-700' : 'text-amber-700'}`}>{c.fit_score}</span>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-[#0A1F44] truncate">{c.name}</span>
                <span className="px-1.5 py-0.5 bg-[#D4AF37]/15 text-[#8B7425] text-[7px] font-bold rounded uppercase tracking-wide">Recommended</span>
                {c.batch && c.batch !== 'v1' && <span className="px-1 py-0.5 bg-indigo-50 text-indigo-600 text-[7px] font-bold rounded">{c.batch}</span>}
                {c.sourcing_pass === 'pass_2' && <span className="px-1 py-0.5 bg-violet-50 text-violet-600 text-[7px] font-bold rounded">Expansion</span>}
                {c.in_submission && <span className="px-1 py-0.5 bg-emerald-100 text-emerald-700 text-[7px] font-bold rounded">Submission</span>}
              </div>
              <p className="text-[10px] text-gray-500 truncate">{c.title}</p>
              <p className="text-[10px] text-gray-400 truncate">{c.current_company} {c.location && `\u2022 ${c.location}`}</p>
              {(c.mismatch_reasons || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {c.mismatch_reasons.map((m, mi) => (
                    <span key={mi} className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-50 text-red-600 text-[8px] rounded" data-testid={`mismatch-rec-${roleIndex}-${candidateIndex}-${mi}`}>
                      <AlertTriangle className="h-2 w-2" />{m}
                    </span>
                  ))}
                </div>
              )}
              <ScoreBreakdown breakdown={c.score_breakdown} totalScore={c.fit_score} compact />
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 ml-3 flex-shrink-0">
            <CandidateActions candidate={c}
              onNoteAdded={onNoteAdded}
              onSubmissionToggled={onSubmissionToggled}
              onStageChanged={onStageChanged}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group p-2.5 bg-white border border-gray-200 rounded-lg" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {selectable && (
            <input type="checkbox" checked={!!selected} onChange={() => onToggleSelect && onToggleSelect(c)}
              className="h-4 w-4 rounded border-gray-300 text-[#D4AF37] focus:ring-[#D4AF37] flex-shrink-0 cursor-pointer"
              onClick={e => e.stopPropagation()} data-testid={`select-other-${roleIndex}-${candidateIndex}`} />
          )}
          <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ${c.fit_score >= 50 ? 'bg-emerald-100' : c.fit_score >= 30 ? 'bg-amber-100' : 'bg-gray-100'}`}>
            <span className={`text-[10px] font-bold ${c.fit_score >= 50 ? 'text-emerald-700' : c.fit_score >= 30 ? 'text-amber-700' : 'text-gray-600'}`}>{c.fit_score}</span>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-[#0A1F44] truncate">{c.name}</span>
              {c.batch && c.batch !== 'v1' && <span className="px-1 py-0.5 bg-indigo-50 text-indigo-600 text-[7px] font-bold rounded">{c.batch}</span>}
              {c.sourcing_pass === 'pass_2' && <span className="px-1 py-0.5 bg-violet-50 text-violet-600 text-[7px] font-bold rounded">Expansion</span>}
              {c.in_submission && <span className="px-1 py-0.5 bg-emerald-100 text-emerald-700 text-[7px] font-bold rounded">Submission</span>}
            </div>
            <p className="text-[10px] text-gray-500 truncate">{c.title}</p>
            <p className="text-[10px] text-gray-400 truncate">{c.current_company} {c.location && `\u2022 ${c.location}`}</p>
            {(c.mismatch_reasons || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-0.5">
                {c.mismatch_reasons.map((m, mi) => (
                  <span key={mi} className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-50 text-red-600 text-[8px] rounded" data-testid={`mismatch-${roleIndex}-${candidateIndex}-${mi}`}>
                    <AlertTriangle className="h-2 w-2" />{m}
                  </span>
                ))}
              </div>
            )}
            <ScoreBreakdown breakdown={c.score_breakdown} totalScore={c.fit_score} compact />
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 ml-3 flex-shrink-0">
          <CandidateActions candidate={c}
            onNoteAdded={onNoteAdded}
            onSubmissionToggled={onSubmissionToggled}
            onStageChanged={onStageChanged}
          />
        </div>
      </div>
    </div>
  );
};
